import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Loader2, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import * as React from 'react';
import { authClient } from '@/lib/auth-client';
import { AuthLayout } from '@/components/auth/auth-layout';
import { PasswordInput } from '@/components/auth/password-input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { usePasswordStrength } from '@/hooks/use-password-strength';

export const Route = createFileRoute('/reset-password')({
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      throw redirect({ to: '/dashboard' });
    }
  },
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || '',
    error: (search.error as string) || '',
  }),
  component: ResetPasswordPage,
});

type Status = 'form' | 'success' | 'error';

function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, error: urlError } = Route.useSearch();
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [status, setStatus] = React.useState<Status>(urlError ? 'error' : 'form');
  const [errorMessage, setErrorMessage] = React.useState(
    urlError ? t('auth.resetPassword.tokenExpired') : '',
  );
  const strengthScore = usePasswordStrength(newPassword);

  const canSubmit =
    token.length > 0 &&
    newPassword.length >= 8 &&
    strengthScore >= 2 &&
    newPassword === confirmPassword;

  const passwordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const baseURL = import.meta.env.VITE_API_BASE_URL
        ? new URL(import.meta.env.VITE_API_BASE_URL).origin
        : window.location.origin;
      const res = await fetch(`${baseURL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ newPassword, token }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.code || body?.error?.code || '';
        if (code === 'TOKEN_EXPIRED' || code === 'INVALID_TOKEN') {
          setErrorMessage(t('auth.resetPassword.tokenExpired'));
        } else {
          setErrorMessage(body?.message || t('auth.resetPassword.error'));
        }
        setStatus('error');
        return;
      }

      setStatus('success');
    } catch {
      setErrorMessage(t('auth.resetPassword.error'));
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }

  const backLink = (
    <Link
      to="/login"
      className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
    >
      <ArrowLeft className="size-3.5" />
      {t('auth.backToLogin')}
    </Link>
  );

  if (status === 'success') {
    return (
      <AuthLayout
        title={t('auth.resetPassword.successTitle')}
        description={t('auth.resetPassword.successDescription')}
        backLink={backLink}
      >
        <div className="space-y-5">
          <div className="flex items-center justify-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle className="size-7 text-green-500" />
            </div>
          </div>
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.resetPassword.successMessage')}
          </p>
          <Button
            className="h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
            size="lg"
            onClick={() => navigate({ to: '/login' })}
          >
            {t('auth.resetPassword.goToLogin')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (status === 'error') {
    return (
      <AuthLayout
        title={t('auth.resetPassword.errorTitle')}
        description={errorMessage}
        backLink={backLink}
      >
        <div className="space-y-5">
          <div className="flex items-center justify-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="size-7 text-destructive" />
            </div>
          </div>
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.resetPassword.tryAgain')}
          </p>
          <Button
            variant="outline"
            className="h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
            size="lg"
            onClick={() => navigate({ to: '/forgot-password' })}
          >
            {t('auth.resetPassword.requestNewLink')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.resetPassword.title')}
      description={t('auth.resetPassword.description')}
      backLink={backLink}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-sm font-medium text-foreground">
              {t('auth.resetPassword.newPassword')}
            </Label>
            <PasswordInput
              id="newPassword"
              showStrength
              strengthScore={strengthScore}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
              className="h-12 rounded-lg bg-background text-sm"
              autoComplete="new-password"
            />
            {newPassword.length > 0 && newPassword.length < 8 && (
              <p className="text-xs text-destructive">{t('profile.passwordMinLength')}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
              {t('auth.resetPassword.confirmPassword')}
            </Label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
              className="h-12 rounded-lg bg-background text-sm"
              autoComplete="new-password"
            />
            {passwordMismatch && (
              <p className="text-xs text-destructive">{t('profile.passwordMismatch')}</p>
            )}
          </div>
        </div>

        <Button
          type="submit"
          className="mt-8 h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
          size="lg"
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t('auth.resetPassword.submitting')}
            </>
          ) : (
            t('auth.resetPassword.submit')
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
